import React, { useContext } from 'react';
import { t } from '@sm/intl';
import { createURL } from '@sm/utils';
import { LogoWithText } from '@sm/webassets/Logos';
import { StaticContext } from '@sm/webassets/StaticContext';
import { generateMetricsAttribute } from '@sm/metrics';
import { UseExperimentResult } from '@sm/webassets/Experiments';
import { Box } from '@wds/box';
import { Button } from '@wds/button';
import { Grid } from '@wds/grid';
import { Typography } from '@wds/typography';

import EndpageHeader from '~app/components/EndpageHeader';
import EndpageImage from '~app/components/EndpageImage';
import TextBanner from '~app/components/TextBanner';

import Pages, { Page } from './pages';

import useStyles from './useStyles';
import { STANDARD_ENDPAGE_DEFAULT_COPY } from './copy';

export type Props = {
  pageData?: Page;
  pageName: string;
  treatmentName?: string;
  treatments?: UseExperimentResult;
  utSource: string;
};

export default function StandardEndpage({
  pageData = Pages,
  pageName,
  treatmentName,
  treatments,
  utSource,
}: Props): React.ReactElement {
  const {
    imageSource,
    logoRoute,
    pageCTA,
    pageCTARoute,
    pageCtaUsage,
    pageHeading,
    pageSubheading,
    privacyNotice,
    shadedBackgroundColor,
    textBannerBackgroundColor,
    textBannerCTA,
    textBannerCTARoute,
    textBannerCtaUsage,
    textBannerHeading,
    textBannerSubheading,
    utSource2,
  } = pageData[pageName];
  const utSource3 = 'StandardEndpage';
  const { container, headingText, logo, negativeMargin, subheadingText } = useStyles({ shadedBackgroundColor });
  const textColor = shadedBackgroundColor?.startsWith('dark') ? 'light' : 'dark';
  const logoColor = shadedBackgroundColor?.startsWith('dark') ? 'alt' : 'primary';

  const {
    smParams: { surveyId, collectorId, respondentId },
    templateType,
    pollingSurveyId,
    cParam,
  } = useContext(StaticContext);

  const amplitudeEventName = 'endpage user interaction';
  return (
    <>
      <EndpageHeader
        copy={t(STANDARD_ENDPAGE_DEFAULT_COPY.ENDPAGE_HEADER)}
        hasLogo
        headerColor="sabaeus"
        headerType="thanks"
      />
      <Box className={container} display="flex" flexAlign="center" flexDirection="column" pt={6} px={5}>
        <Grid noBleeds noGutters>
          <Grid.Item lg={3} md={1}>
            <></>
          </Grid.Item>
          <Grid.Item lg={6} md={10} xs={12}>
            <>
              <a
                aria-label={t(STANDARD_ENDPAGE_DEFAULT_COPY.LOGO_ARIA_LABEL)}
                data-testid="StandardEndpage__LogoWithText"
                href={createURL(logoRoute, {
                  ut_source: utSource,
                  ut_source2: utSource2,
                  ut_source3: utSource3,
                  ut_ctatext: 'LogoWithText',
                })}
                data-sm-metrics={generateMetricsAttribute({
                  data: {
                    amplitudeEvent: amplitudeEventName,
                    itemSelected: 'logo',
                    templateType,
                    surveyId,
                    collectorId,
                    respondentId,
                    log: {
                      message: `${utSource}-${utSource2}-${utSource3}-LogoWithText`,
                    },
                  },
                })}
              >
                <LogoWithText className={logo} color={logoColor} />
              </a>
              <Box mt={7}>
                <Typography align="center" color={textColor} variant="sectionTitleSm">
                  <span className={headingText}>{t(pageHeading)}</span>
                </Typography>
              </Box>
              {pageSubheading && (
                <Box mt={2}>
                  <Typography align="center" color={textColor}>
                    <span className={subheadingText}>{t(pageSubheading)}</span>
                  </Typography>
                </Box>
              )}
            </>
          </Grid.Item>
          <Grid.Item lg={3} md={1}>
            <></>
          </Grid.Item>
        </Grid>
        <Box mt={5} mb={5}>
          {pageCTARoute && (
            <Button
              color="upgrade"
              href={createURL(pageCTARoute, {
                ut_source: utSource,
                ut_source2: utSource2,
                ut_source3: utSource3,
                ut_ctatext: pageCTA.defaultMessage ?? '',
              })}
              size="lg"
              data-sm-metrics={generateMetricsAttribute({
                data: {
                  amplitudeEvent: amplitudeEventName,
                  itemSelected: pageCtaUsage,
                  templateType,
                  surveyId,
                  collectorId,
                  respondentId,
                  log: {
                    message: `${utSource}-${utSource2}-${utSource3}-${pageCTA.defaultMessage}`,
                  },
                },
              })}
            >
              {t(pageCTA)}
            </Button>
          )}
          {pollingSurveyId && (
            <>
              <Button
                color="upgrade"
                href={createURL(`/r/${pollingSurveyId}`, {
                  c: cParam,
                  ut_source: utSource,
                  ut_source2: utSource2,
                  ut_source3: utSource3,
                  ut_ctatext: pageCTA?.defaultMessage ?? '',
                })}
                data-sm-metrics={generateMetricsAttribute({
                  data: {
                    amplitudeEvent: amplitudeEventName,
                    itemSelected: pageCtaUsage,
                    templateType,
                    surveyId,
                    collectorId,
                    respondentId,
                    log: {
                      message: `${utSource}-${utSource2}-${utSource3}-${pageCTA?.defaultMessage}-${treatmentName}`,
                    },
                  },
                })}
              >
                {t(pageCTA)}
              </Button>
            </>
          )}
        </Box>
        <Grid noBleeds noGutters>
          <Grid.Item lg={3} md={1}>
            <></>
          </Grid.Item>
          <Grid.Item lg={6} md={10} xs={12}>
            <>
              {privacyNotice && (
                <Box mb={5}>
                  <Typography align="center">{t(...privacyNotice, { html: true })}</Typography>
                </Box>
              )}
            </>
          </Grid.Item>
          <Grid.Item lg={3} md={1}>
            <></>
          </Grid.Item>
        </Grid>
      </Box>
      <Box className={negativeMargin} mb={5}>
        <EndpageImage imageSource={imageSource} backgroundColor={shadedBackgroundColor} />
      </Box>
      {textBannerHeading && textBannerSubheading && (
        <TextBanner
          backgroundColor={textBannerBackgroundColor}
          ctaText={textBannerCTA}
          ctaUsage={textBannerCtaUsage}
          heading={t(textBannerHeading)}
          route={textBannerCTARoute}
          subheading={t(textBannerSubheading)}
          utSource={utSource}
          utSource2={utSource2}
        />
      )}
      {!textBannerHeading && textBannerSubheading && (
        <TextBanner
          backgroundColor={textBannerBackgroundColor}
          ctaText={textBannerCTA}
          ctaUsage={textBannerCtaUsage}
          route={textBannerCTARoute}
          subheading={t(textBannerSubheading)}
          utSource={utSource}
          utSource2={utSource2}
        />
      )}
      {textBannerHeading && !textBannerSubheading && (
        <TextBanner
          backgroundColor={textBannerBackgroundColor}
          ctaText={textBannerCTA}
          ctaUsage={textBannerCtaUsage}
          heading={t(textBannerHeading)}
          route={textBannerCTARoute}
          utSource={utSource}
          utSource2={utSource2}
        />
      )}
    </>
  );
}
